/**
 * Сравнение с предыдущим периодом одним механизмом.
 *
 * Первая версия жила прямо в App.tsx и умела только плитки Overview. Заказчик
 * это заметил сразу: «кажется работает только на странице Overview». Причина
 * была в том, что сравнение знало ФОРМУ ответа Overview — а у каждого экрана она
 * своя, и повторять эту логику пять раз означало пять мест, где она разойдётся.
 *
 * Здесь она одна. Экран получает готовую функцию и на каждой плитке пишет одну
 * строку — путь к своему полю. Ни ответ, ни расчёты при этом не меняются: и
 * текущий, и прошлый период приходят ТЕМ ЖЕ запросом с теми же определениями,
 * отличается только диапазон дат.
 */

/**
 * `kind` говорит, ЧЕМ измерена величина, чтобы значок называл базу теми же
 * единицами, что и само число:
 *   money  — минорные единицы (AED ×100), печатать через `money()`;
 *   rate   — доля в процентах, разницу считать В ПУНКТАХ, а не в процентах от
 *            процента: рост охвата с 1,7 % до 2,0 % это «+0,3 пункта», а
 *            «+18 %» прочитают как рост охвата до восемнадцати;
 *   count  — всё остальное.
 */
export type DeltaKind = "money" | "rate" | "count";

export type Delta = { value: number; prev: number; inverse?: boolean; kind: DeltaKind };

/**
 * Вид величины по имени поля. Опирается на соглашение проекта: деньги всегда в
 * полях `*_minor`, доли — в полях `*_pct`. Соглашение соблюдается везде, поэтому
 * разбор пути надёжнее, чем признак, проставленный руками на каждой плитке.
 */
function kindOf(path: string): DeltaKind {
  if (path.endsWith("_minor")) return "money";
  if (path.endsWith("_pct")) return "rate";
  return "count";
}

/**
 * `path` — путь через точку в ответе экрана: `kpi.reached_handoff`,
 * `deposits.deposit_all_aed_minor`. Путь, а не колбэк: колбэк пришлось бы
 * писать дважды, для текущего ответа и для прошлого, и они однажды разошлись бы.
 */
export type DeltaFn = (path: string, opts?: { inverse?: boolean }) => Delta | undefined;

function dig(root: unknown, path: string): unknown {
  let cur = root;
  for (const key of path.split(".")) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/**
 * Числа приходят и числами, и строками: Postgres отдаёт `bigint` и `numeric`
 * строкой, потому что они не влезают в double без потерь. Деньги в системе
 * целые в минорных единицах, поэтому разбор строки здесь безопасен.
 */
function numeric(v: unknown): number | undefined {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/**
 * Возвращает функцию сравнения. Если прошлого периода нет — а его нет, пока
 * сравнение выключено или пока ответ не пришёл, — функция отдаёт `undefined`, и
 * плитка просто не показывает значка. Это важнее, чем кажется: сравнение это
 * добавка к числу, и его отсутствие не должно ни ломать экран, ни печатать ноль,
 * который прочитают как измеренное значение.
 */
export function makeDelta(now: unknown, prev: unknown): DeltaFn {
  if (prev === null || prev === undefined) return () => undefined;
  return (path, opts) => {
    const a = numeric(dig(now, path));
    const b = numeric(dig(prev, path));
    if (a === undefined || b === undefined) return undefined;
    const kind = kindOf(path);
    return opts?.inverse === true
      ? { value: a, prev: b, kind, inverse: true }
      : { value: a, prev: b, kind };
  };
}
